/// <amd-module name="Feed/Legacy/Protocols/NuGet/Commands/NuGetRelistCommand" />
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import { PackageVersionDetails } from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import * as PackageResources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { IPackagePayload } from "Feed/Legacy/Common/ActionPayloads";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { NuGetCiConstants } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { IFeedNuGetDataService } from "Feed/Legacy/Protocols/NuGet/NuGetDataService";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class RelistPackagesCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly actionMethod: () => void;
    public readonly disabled: boolean;

    constructor(id: string, displayText: string, dialogProps: IGeneralDialogProps, disabled: boolean) {
        this.id = id;
        this.protocolCommand = ProtocolCommands.Relist;
        this.displayText = displayText;
        this.icon = "bowtie-icon bowtie-check";
        this.disabled = disabled;
        this.actionMethod = () => Actions.DialogOpenChanged.invoke(dialogProps);
    }
}

// TODO: This class is used by the old dialogs and can be removed when the feature flag for new dialogs is removed.
export class RelistPackageCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly actionMethod: () => void;
    public readonly disabled: boolean;

    constructor(
        pageContext: IVssPageContext,
        onRelistPackage: (
            pageContext: IVssPageContext,
            packageSummary: Package,
            packageVersion: PackageVersion,
            feed: Feed,
            viaPackageList: boolean
        ) => Promise<void>,
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean,
        disabled: boolean
    ) {
        this.id = "relist";
        this.protocolCommand = ProtocolCommands.Relist;
        this.displayText = PackageResources.PackageCommands_RelistPackage;
        this.icon = "bowtie-icon bowtie-check";
        this.disabled = disabled;
        this.actionMethod = () => onRelistPackage(pageContext, packageSummary, packageVersion, feed, viaPackageList);
    }

    public static onRelistPackage(
        pageContext: IVssPageContext,
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean
    ): Promise<void> {
        const nuGetDataService = pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");

        CustomerIntelligenceHelper.publishEvent(pageContext, NuGetCiConstants.RelistPackage, {
            feedId: feed.id,
            feedName: feed.name,
            viewId: feed.view ? feed.view.id : undefined,
            viewName: feed.view ? feed.view.name : undefined,
            packageName: packageSummary.name,
            packageVersion: packageVersion.version
        });

        const packageVersionDetails = {
            listed: true
        } as PackageVersionDetails;

        const projectId = feed.project ? feed.project.id : null;
        return nuGetDataService
            .updatePackageVersion(
                packageVersionDetails,
                projectId,
                feed.id,
                packageSummary.normalizedName,
                packageVersion.normalizedVersion
            )
            .then(
                success => {
                    if (!viaPackageList) {
                        packageVersion.isListed = true;
                        const packagePayload = {
                            packageSummary,
                            packageVersion
                        } as IPackagePayload;

                        Actions.PackageModified.invoke(packagePayload);
                        announce(
                            Utils_String.format(
                                PackageResources.PackageRelisted_Announcement_Version_One,
                                packageVersion.version
                            )
                        );
                    } else {
                        Actions.PackageListedStatusChanged.invoke({
                            packageId: packageSummary.id,
                            isListed: true
                        });
                        announce(
                            Utils_String.format(PackageResources.PackageRelisted_Announcement_One, packageSummary.name)
                        );
                    }
                },
                err => {
                    if (err.status === 401) {
                        Actions.ErrorEncountered.invoke({
                            message: PackageResources.Error_ErrorUserUnauthorized,
                            details: err
                        } as IError);
                    } else {
                        Actions.ErrorEncountered.invoke({
                            message:
                                err.serverError && err.serverError.reason
                                    ? err.serverError.reason
                                    : PackageResources.Error_ErrorRelistingPackage,
                            isCritical: true,
                            details: err
                        } as IError);
                    }
                }
            );
    }
}
